import { useState, type FormEvent, type ReactElement } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";

import { apiClient } from "../api/client";
import { AdminNav } from "./AdminPage";
import { RosiinkaPanel } from "./RosiinkaPanel";

/**
 * Cycle 22 STORY-N2 (ADR-008 Phase 4 frontend) — Admin Tenants management UI.
 *
 * Backend endpoints (cycle 22 STORY-K3):
 *   POST   /api/admin/tenants/dry-run  — preview без DB
 *   POST   /api/admin/tenants          — provision new tenant
 *   GET    /api/admin/tenants          — list all
 *   GET    /api/admin/tenants/{slug}   — single tenant detail
 *
 * Allows super-admin to onboard pilot customers via web UI без curl/psql.
 */

type TenantPlan = "trial" | "free" | "pro" | "enterprise";

const PLAN_LABELS: Record<TenantPlan, { ru: string; color: string; bg: string }> = {
  trial: { ru: "Trial (14 дней)", color: "#a07000", bg: "#fff7d6" },
  free: { ru: "Free", color: "#666", bg: "#eee" },
  pro: { ru: "Pro", color: "#0a7a4a", bg: "#e6f7ee" },
  enterprise: { ru: "Enterprise", color: "#0b5fff", bg: "#e6f0ff" },
};

type Tenant = {
  id: string;
  slug: string;
  name: string;
  plan: string;
  is_active: boolean;
  contact_email: string | null;
  created_at: string;
};

type TenantList = {
  items: Tenant[];
  total: number;
};

type DryRunResult = {
  proposed_slug: string;
  proposed_admin_email: string;
  warnings: string[];
};

export function AdminTenantsPage(): ReactElement {
  const queryClient = useQueryClient();

  // Provision form state
  const [companyName, setCompanyName] = useState("");
  const [adminEmail, setAdminEmail] = useState("");
  const [plan, setPlan] = useState<TenantPlan>("trial");
  const [contactPhone, setContactPhone] = useState("");
  const [dryRun, setDryRun] = useState<DryRunResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [rosiinkaSlug, setRosiinkaSlug] = useState("");

  // apiClient interceptor автоматически добавляет Authorization: Bearer <jwt>
  // из localStorage — manual headers не нужны.

  // List of tenants
  const tenantsQuery = useQuery<TenantList>({
    queryKey: ["admin-tenants"],
    queryFn: async () => {
      const r = await apiClient.get("/api/admin/tenants");
      return r.data;
    },
  });

  // Dry run mutation
  const dryRunMutation = useMutation({
    mutationFn: async () => {
      const r = await apiClient.post("/api/admin/tenants/dry-run", {
        company_name: companyName,
        admin_email: adminEmail,
        plan,
        contact_phone: contactPhone || undefined,
      });
      return r.data as DryRunResult;
    },
    onSuccess: (data) => {
      setDryRun(data);
      setError(null);
    },
    onError: (err: any) => {
      setError(err?.response?.data?.detail || err.message || "Ошибка валидации");
      setDryRun(null);
    },
  });

  // Provision mutation
  const provisionMutation = useMutation({
    mutationFn: async () => {
      const r = await apiClient.post("/api/admin/tenants", {
        company_name: companyName,
        admin_email: adminEmail,
        plan,
        contact_phone: contactPhone || undefined,
      });
      return r.data as Tenant;
    },
    onSuccess: (tenant) => {
      setSuccess(`Tenant ${tenant.slug} создан успешно`);
      setCompanyName("");
      setAdminEmail("");
      setContactPhone("");
      setDryRun(null);
      setError(null);
      queryClient.invalidateQueries({ queryKey: ["admin-tenants"] });
    },
    onError: (err: any) => {
      setError(err?.response?.data?.detail || err.message || "Ошибка создания");
      setSuccess(null);
    },
  });

  const handleDryRun = (e: FormEvent) => {
    e.preventDefault();
    setSuccess(null);
    dryRunMutation.mutate();
  };

  const handleProvision = (e: FormEvent) => {
    e.preventDefault();
    if (!dryRun) {
      setError("Сначала проверьте через 'Preview'");
      return;
    }
    provisionMutation.mutate();
  };

  return (
    <div style={{ padding: "20px", fontFamily: "system-ui" }}>
      <AdminNav />
      <h1>Tenants (клиенты)</h1>
      <p style={{ color: "#666" }}>
        Управление организациями-клиентами. Каждый tenant = изолированная зона
        данных. Создание = onboarding нового pilot customer.
      </p>

      <section style={{ marginTop: "30px", padding: "20px", border: "1px solid #ddd", borderRadius: "8px" }}>
        <h2>Создать нового tenant</h2>

        <form onSubmit={handleDryRun} style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px" }}>
          <label>
            Компания (название) *
            <input
              type="text"
              value={companyName}
              onChange={(e) => setCompanyName(e.target.value)}
              required
              placeholder="Acme Corp"
              style={inputStyle}
            />
          </label>
          <label>
            Admin email *
            <input
              type="email"
              value={adminEmail}
              onChange={(e) => setAdminEmail(e.target.value)}
              required
              placeholder="ceo@acme.example"
              style={inputStyle}
            />
          </label>
          <label>
            Тариф
            <select value={plan} onChange={(e) => setPlan(e.target.value as TenantPlan)} style={inputStyle}>
              <option value="trial">Trial (14 дней)</option>
              <option value="free">Free</option>
              <option value="pro">Pro</option>
              <option value="enterprise">Enterprise</option>
            </select>
          </label>
          <label>
            Контактный телефон (опционально)
            <input
              type="tel"
              value={contactPhone}
              onChange={(e) => setContactPhone(e.target.value)}
              placeholder="+7 (499) 286-85-30"
              style={inputStyle}
            />
          </label>

          <div style={{ gridColumn: "1 / span 2", display: "flex", gap: "12px" }}>
            <button type="submit" disabled={dryRunMutation.isPending} style={btnPreview}>
              {dryRunMutation.isPending ? "Проверка..." : "👁 Preview (без создания)"}
            </button>
            <button
              type="button"
              onClick={handleProvision}
              disabled={!dryRun || provisionMutation.isPending}
              style={dryRun ? btnCreate : btnDisabled}
            >
              {provisionMutation.isPending ? "Создание..." : "✓ Создать tenant"}
            </button>
          </div>
        </form>

        {error && (
          <div style={{ marginTop: "12px", padding: "10px", background: "#fde8eb", color: "#a01c2a", borderRadius: "4px" }}>
            ❌ {error}
          </div>
        )}

        {success && (
          <div style={{ marginTop: "12px", padding: "10px", background: "#d6f5e3", color: "#0a7a4a", borderRadius: "4px" }}>
            ✅ {success}
          </div>
        )}

        {dryRun && !success && (
          <div style={{ marginTop: "16px", padding: "14px", background: "#e6f0ff", borderRadius: "4px" }}>
            <strong>Preview результата:</strong>
            <div style={{ marginTop: "8px" }}>
              <div>
                <span style={{ color: "#666" }}>Slug:</span>{" "}
                <code style={{ background: "#fff", padding: "2px 6px", borderRadius: "2px" }}>
                  {dryRun.proposed_slug}
                </code>
              </div>
              <div style={{ marginTop: "4px" }}>
                <span style={{ color: "#666" }}>URL клиента (path-based, активно сейчас):</span>{" "}
                <a
                  href={`/t/${dryRun.proposed_slug}`}
                  target="_blank"
                  rel="noreferrer"
                >
                  /t/{dryRun.proposed_slug}
                </a>
              </div>
              <div style={{ marginTop: "4px", color: "#666", fontSize: "13px" }}>
                <span>URL клиента (subdomain, после активации wildcard DNS):</span>{" "}
                <code style={{ background: "#f5f5f5", padding: "2px 6px", borderRadius: "2px" }}>
                  https://{dryRun.proposed_slug}.it-navigator.online
                </code>
              </div>
              <div style={{ marginTop: "4px" }}>
                <span style={{ color: "#666" }}>Admin email:</span> {dryRun.proposed_admin_email}
              </div>
            </div>

            {dryRun.warnings.length > 0 && (
              <ul style={{ marginTop: "12px", color: "#a07000" }}>
                {dryRun.warnings.map((w, i) => (
                  <li key={i}>⚠️ {w}</li>
                ))}
              </ul>
            )}
          </div>
        )}
      </section>

      <section style={{ marginTop: "30px" }}>
        <h2>Существующие tenants</h2>

        {tenantsQuery.isLoading && <p>Загрузка...</p>}
        {tenantsQuery.isError && <p style={{ color: "red" }}>Ошибка загрузки списка.</p>}

        {tenantsQuery.data && (
          <>
            <p style={{ color: "#666" }}>
              Всего: <strong>{tenantsQuery.data.total}</strong>
            </p>
            <table style={{ width: "100%", borderCollapse: "collapse", marginTop: "12px" }}>
              <thead>
                <tr style={{ background: "#f5f5f5", textAlign: "left" }}>
                  <th style={th}>Slug</th>
                  <th style={th}>Название</th>
                  <th style={th}>Тариф</th>
                  <th style={th}>Email</th>
                  <th style={th}>Статус</th>
                  <th style={th}>Создан</th>
                  <th style={th}>Действия</th>
                </tr>
              </thead>
              <tbody>
                {tenantsQuery.data.items.map((t) => {
                  const planMeta = PLAN_LABELS[t.plan as TenantPlan] || {
                    ru: t.plan,
                    color: "#666",
                    bg: "#eee",
                  };
                  const isLegacy = t.slug === "legacy-default";
                  return (
                    <tr key={t.id} style={{ borderBottom: "1px solid #eee" }}>
                      <td style={td}>
                        <a href={`/t/${t.slug}`} target="_blank" rel="noreferrer" title={`Open ${t.slug} landing`}>
                          <code>{t.slug}</code>
                        </a>
                      </td>
                      <td style={td}>{t.name}</td>
                      <td style={td}>
                        <span style={{ ...badge, background: planMeta.bg, color: planMeta.color }}>
                          {planMeta.ru}
                        </span>
                      </td>
                      <td style={td}>{t.contact_email || "—"}</td>
                      <td style={td}>
                        {t.is_active ? (
                          <span style={{ color: "#0a7a4a" }}>Активен</span>
                        ) : (
                          <span style={{ color: "#a01c2a" }}>Заблокирован</span>
                        )}
                      </td>
                      <td style={td}>{new Date(t.created_at).toLocaleDateString("ru-RU")}</td>
                      <td style={td}>
                        {!isLegacy && (
                          <TenantRowActions tenant={t} />
                        )}
                        {isLegacy && (
                          <span style={{ color: "#888", fontSize: "12px" }}>системный</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </>
        )}
      </section>

      <section style={{ marginTop: "40px", paddingTop: "20px", borderTop: "2px solid #eee" }}>
        <h2>🤖 Росиинка — рабочий продукт клиента</h2>
        <p style={{ color: "#666" }}>
          Активируйте Росиинку (AI-API) в пространстве клиента: провижининг, разовый
          API-ключ, стартовый comp-баланс и интерактивные инструкции (ADR-009).
        </p>
        <label style={{ display: "block", maxWidth: "420px", marginBottom: "16px" }}>
          Клиент (tenant)
          <select
            value={rosiinkaSlug}
            onChange={(e) => setRosiinkaSlug(e.target.value)}
            style={inputStyle}
          >
            <option value="">— выберите клиента —</option>
            {tenantsQuery.data?.items
              .filter((t) => t.slug !== "legacy-default")
              .map((t) => (
                <option key={t.id} value={t.slug}>
                  {t.name} ({t.slug})
                </option>
              ))}
          </select>
        </label>
        {rosiinkaSlug && (
          <RosiinkaPanel
            tenantSlug={rosiinkaSlug}
            tenantName={
              tenantsQuery.data?.items.find((t) => t.slug === rosiinkaSlug)?.name
            }
          />
        )}
      </section>
    </div>
  );
}

// ============================================================
// TenantRowActions — Edit + Suspend buttons + inline edit modal
// (Cycle 22 STORY-X3)
// ============================================================

function TenantRowActions({ tenant }: { tenant: Tenant }): ReactElement {
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(tenant.name);
  const [plan, setPlan] = useState<TenantPlan>(tenant.plan as TenantPlan);
  const [contactEmail, setContactEmail] = useState(tenant.contact_email ?? "");

  const updateMutation = useMutation({
    mutationFn: async () => {
      const r = await apiClient.patch(`/api/admin/tenants/${tenant.slug}`, {
        name,
        plan,
        contact_email: contactEmail || null,
      });
      return r.data as Tenant;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-tenants"] });
      setEditing(false);
    },
  });

  const suspendMutation = useMutation({
    mutationFn: async () => {
      const r = await apiClient.delete(`/api/admin/tenants/${tenant.slug}`);
      return r.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-tenants"] });
    },
  });

  const reactivateMutation = useMutation({
    mutationFn: async () => {
      const r = await apiClient.patch(`/api/admin/tenants/${tenant.slug}`, {
        is_active: true,
      });
      return r.data as Tenant;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-tenants"] });
    },
  });

  if (editing) {
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Название"
          style={{ ...inputStyle, marginTop: 0, padding: "4px 6px", fontSize: "12px" }}
        />
        <select
          value={plan}
          onChange={(e) => setPlan(e.target.value as TenantPlan)}
          style={{ ...inputStyle, marginTop: 0, padding: "4px 6px", fontSize: "12px" }}
        >
          <option value="trial">Trial</option>
          <option value="free">Free</option>
          <option value="pro">Pro</option>
          <option value="enterprise">Enterprise</option>
        </select>
        <input
          value={contactEmail}
          onChange={(e) => setContactEmail(e.target.value)}
          placeholder="Email"
          style={{ ...inputStyle, marginTop: 0, padding: "4px 6px", fontSize: "12px" }}
        />
        <div style={{ display: "flex", gap: "4px" }}>
          <button
            onClick={() => updateMutation.mutate()}
            disabled={updateMutation.isPending}
            style={{ ...btnSmall, background: "#0a7a4a", color: "#fff" }}
          >
            {updateMutation.isPending ? "..." : "✓"}
          </button>
          <button
            onClick={() => setEditing(false)}
            style={{ ...btnSmall, background: "#ccc" }}
          >
            ×
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ display: "flex", gap: "4px" }}>
      <button onClick={() => setEditing(true)} style={btnSmall} title="Изменить">
        ✏️
      </button>
      {tenant.is_active ? (
        <button
          onClick={() => {
            if (confirm(`Заблокировать tenant ${tenant.slug}?`)) {
              suspendMutation.mutate();
            }
          }}
          disabled={suspendMutation.isPending}
          style={{ ...btnSmall, color: "#a01c2a" }}
          title="Заблокировать"
        >
          {suspendMutation.isPending ? "..." : "🚫"}
        </button>
      ) : (
        <button
          onClick={() => reactivateMutation.mutate()}
          disabled={reactivateMutation.isPending}
          style={{ ...btnSmall, color: "#0a7a4a" }}
          title="Восстановить"
        >
          {reactivateMutation.isPending ? "..." : "↻"}
        </button>
      )}
    </div>
  );
}

const btnSmall: React.CSSProperties = {
  padding: "4px 8px",
  fontSize: "12px",
  border: "1px solid #ddd",
  borderRadius: "3px",
  background: "#fff",
  cursor: "pointer",
};

const inputStyle = {
  display: "block",
  width: "100%",
  marginTop: "4px",
  padding: "8px 10px",
  fontSize: "14px",
  border: "1px solid #ccc",
  borderRadius: "4px",
};

const btnBase = {
  padding: "10px 20px",
  fontSize: "14px",
  borderRadius: "4px",
  cursor: "pointer",
  border: "none",
};

const btnPreview = { ...btnBase, background: "#0b5fff", color: "#fff" };
const btnCreate = { ...btnBase, background: "#0a7a4a", color: "#fff" };
const btnDisabled = { ...btnBase, background: "#ccc", color: "#666", cursor: "not-allowed" };

const th = { padding: "10px 8px", fontWeight: 600, fontSize: "13px", color: "#666" };
const td = { padding: "10px 8px", fontSize: "14px" };
const badge = { padding: "3px 8px", borderRadius: "12px", fontSize: "12px", fontWeight: 500 };
